<template>
  <v-main>
    <v-container fluid>
      <!-- Header Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              title="Product Approval"
              subtitle="Review and approve pending products"
            >
              <template v-slot:actions>
                <div class="d-flex justify-end align-center">
                    <v-chip
                      color="warning"
                      outlined
                      small
                      class="font-weight-medium"
                    >
                      <v-icon left x-small>mdi-clock-alert</v-icon>
                      {{ pendingCount }} Pending
                    </v-chip>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20">mdi-filter-variant</v-icon>
                    <span class="text-body-2 font-weight-medium">Filter Products</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Products"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    @input="handleSearch"
                  ></v-text-field>
                </v-col>

                <!-- Category Filter -->
                <v-col cols="12" md="4">
                  <v-select
                    v-model="filter.category_id"
                    :items="categories"
                    item-text="name"
                    item-value="id"
                    label="Filter by Category"
                    prepend-inner-icon="mdi-shape"
                    outlined
                    dense
                    clearable
                    @change="applyFilters"
                  ></v-select>
                </v-col>

                <!-- Sort Options -->
                <v-col cols="12" md="4">
                  <v-select
                    v-model="filter.sort_by"
                    :items="sortOptions"
                    label="Sort by"
                    prepend-inner-icon="mdi-sort"
                    outlined
                    dense
                    @change="applyFilters"
                  ></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <v-data-table
              :headers="headers"
              :items="products"
              :loading="loading"
              :items-per-page="rowsPerPage"
              :page.sync="currentPage"
              :server-items-length="totalItems"
              hide-default-footer
              class="elevation-1"
              item-key="id"
            >
              <!-- Loading slot -->
              <template #loading>
                <div class="text-center pa-4">
                  <health-loader message="Loading products..." />
                </div>
              </template>

              <!-- No data slot -->
              <template #no-data>
                <div class="text-center pa-8">
                  <v-icon color="grey lighten-1" size="60">mdi-package-variant</v-icon>
                  <div class="text-h6 grey--text mt-4">No pending products found</div>
                  <div class="text-body-2 grey--text">All products have been reviewed</div>
                </div>
              </template>

              <!-- Product Info Column -->
              <template #[`item.product_info`]="{ item }">
                <div class="d-flex align-center py-2">
                  <v-avatar size="40" class="mr-3">
                    <v-img
                      v-if="getFirstProductImage(item)"
                      :src="getImageUrl(getFirstProductImage(item))"
                      :alt="item.name"
                    ></v-img>
                    <v-icon v-else color="grey lighten-1" size="20">mdi-package</v-icon>
                  </v-avatar>
                  <div>
                    <div class="font-weight-medium text-subtitle-2">{{ item.name }}</div>
                    <div class="text-caption grey--text" v-if="item.brand_name">
                      Brand: {{ item.brand_name }}
                    </div>
                    <div class="text-caption grey--text" v-if="item.sku_id">
                      SKU: {{ item.sku_id }}
                    </div>
                  </div>
                </div>
              </template>

              <!-- Price Column -->
              <template #[`item.price`]="{ item }">
                <div class="font-weight-bold text-success">
                  {{ item.price ? '$' + parseFloat(item.price).toFixed(2) : 'N/A' }}
                </div>
              </template>

              <!-- Stock Column -->
              <template #[`item.stock`]="{ item }">
                <status-chip :color="getStockColor(item.stock)" icon="mdi-cube" :label="String(parseFloat(item.stock).toFixed(0))" />
              </template>

              <!-- Created By Column -->
              <template #[`item.created_by`]="{ item }">
                <div v-if="item.created_by">
                  <div class="font-weight-medium">{{ item.created_by.user_name }}</div>
                  <div class="text-caption grey--text">{{ item.created_by.email }}</div>
                </div>
                <span v-else class="grey--text">System</span>
              </template>

              <!-- Created At Column -->
              <template #[`item.created_at`]="{ item }">
                <div>
                  <div class="text-body-2">{{ formatDate(item.created_at) }}</div>
                  <div class="text-caption grey--text">{{ formatTime(item.created_at) }}</div>
                </div>
              </template>

              <!-- Actions Column -->
              <template #[`item.actions`]="{ item }">
                <div class="d-flex">
                  <v-btn
                    color="success"
                    small
                    outlined
                    class="mr-1"
                    @click="openApproveDialog(item)"
                    :loading="loadingActions[item.id]"
                  >
                    <v-icon small>mdi-check</v-icon>
                    Approve
                  </v-btn>
                  <v-btn
                    color="error"
                    small
                    outlined
                    @click="openRejectDialog(item)"
                    :loading="loadingActions[item.id]"
                  >
                    <v-icon small>mdi-close</v-icon>
                    Reject
                  </v-btn>
                </div>
              </template>
            </v-data-table>

            <!-- Custom Pagination -->
            <div class="px-4 py-2">
              <v-row align="center" justify="space-between">
                <v-col cols="12" md="6">
                  <div class="d-flex align-center">
                    <span class="text-body-2 mr-3">Rows per page:</span>
                    <v-select
                      v-model="rowsPerPage"
                      :items="[10, 25, 50, 100]"
                      dense
                      outlined
                      hide-details
                      style="max-width: 80px"
                      @change="handlePageSizeChange"
                    ></v-select>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="d-flex justify-end">
                    <v-pagination
                      v-model="currentPage"
                      :length="totalPages"
                      :total-visible="7"
                      @input="handlePageChange"
                    ></v-pagination>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Approve Dialog -->
    <v-dialog v-model="approveDialog.show" max-width="500">
      <v-card>
        <v-card-title class="text-h6 success--text">
          <v-icon left color="success">mdi-check-circle</v-icon>
          Approve Product
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <p>Are you sure you want to approve this product?</p>
            <div class="d-flex align-center mt-3">
              <v-avatar size="32" class="mr-2">
                <v-img
                  v-if="getFirstProductImage(approveDialog.product)"
                  :src="getImageUrl(getFirstProductImage(approveDialog.product))"
                  :alt="approveDialog.product?.name"
                ></v-img>
                <v-icon v-else color="grey lighten-1" size="16">mdi-package</v-icon>
              </v-avatar>
              <div>
                <div class="font-weight-bold">{{ approveDialog.product?.name }}</div>
                <div class="text-caption grey--text">
                  Price: ${{ parseFloat(approveDialog.product?.price || 0).toFixed(2) }}
                </div>
              </div>
            </div>
          </div>
          
          <v-textarea
            v-model="approveDialog.remark"
            label="Approval Remark (Optional)"
            placeholder="Add any notes about this approval..."
            outlined
            rows="3"
            counter="500"
            :rules="[v => !v || v.length <= 500 || 'Remark must be 500 characters or less']"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeApproveDialog">Cancel</v-btn>
          <v-btn 
            color="success" 
            @click="approveProduct"
            :loading="approveDialog.loading"
          >
            <v-icon left>mdi-check</v-icon>
            Approve
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reject Dialog -->
    <v-dialog v-model="rejectDialog.show" max-width="500">
      <v-card>
        <v-card-title class="text-h6 error--text">
          <v-icon left color="error">mdi-close-circle</v-icon>
          Reject Product
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <p>Please provide a reason for rejecting this product:</p>
            <div class="d-flex align-center mt-3">
              <v-avatar size="32" class="mr-2">
                <v-img
                  v-if="getFirstProductImage(rejectDialog.product)"
                  :src="getImageUrl(getFirstProductImage(rejectDialog.product))"
                  :alt="rejectDialog.product?.name"
                ></v-img>
                <v-icon v-else color="grey lighten-1" size="16">mdi-package</v-icon>
              </v-avatar>
              <div>
                <div class="font-weight-bold">{{ rejectDialog.product?.name }}</div>
                <div class="text-caption grey--text">
                  Price: ${{ parseFloat(rejectDialog.product?.price || 0).toFixed(2) }}
                </div>
              </div>
            </div>
          </div>
          
          <v-textarea
            v-model="rejectDialog.remark"
            label="Rejection Reason *"
            placeholder="Please explain why this product is being rejected..."
            outlined
            rows="4"
            counter="1000"
            :rules="rejectRemarkRules"
            :error-messages="rejectDialog.errors.admin_remark"
            required
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeRejectDialog">Cancel</v-btn>
          <v-btn 
            color="error" 
            @click="rejectProduct"
            :loading="rejectDialog.loading"
            :disabled="!rejectDialog.remark || rejectDialog.remark.length < 5"
          >
            <v-icon left>mdi-close</v-icon>
            Reject
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="successSnackbar.show"
      :color="successSnackbar.color"
      :timeout="4000"
      top
    >
      {{ successSnackbar.message }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="successSnackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-main>
</template>

<script>
import axios from '../../axios'

export default {
  name: 'ProductApproval',
  data() {
    return {
      loading: false,
      products: [],
      categories: [],
      totalItems: 0,
      currentPage: 1,
      loadingActions: {},
      
      // Filters
      filter: {
        search_keyword: '',
        category_id: null,
        sort_by: 'created_at',
      },
      
      // Header configuration
      headers: [
        { text: 'Product', value: 'product_info', sortable: false, width: '300px' },
        { text: 'Price', value: 'price', align: 'center', width: '100px' },
        { text: 'Stock', value: 'stock', align: 'center', width: '100px' },
        { text: 'Created By', value: 'created_by', sortable: false, width: '150px' },
        { text: 'Submitted On', value: 'created_at', width: '150px' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center', width: '200px' },
      ],
      
      // Sort options
      sortOptions: [
        { text: 'Oldest First', value: 'created_at' },
        { text: 'Newest First', value: 'created_at_desc' },
        { text: 'Name A-Z', value: 'name' },
        { text: 'Name Z-A', value: 'name_desc' },
        { text: 'Price Low-High', value: 'price' },
        { text: 'Price High-Low', value: 'price_desc' },
      ],
      
      // Dialogs
      approveDialog: {
        show: false,
        product: null,
        remark: '',
        loading: false,
      },
      rejectDialog: {
        show: false,
        product: null,
        remark: '',
        loading: false,
        errors: {},
      },
      
      // Snackbar
      successSnackbar: {
        show: false,
        message: '',
        color: 'success',
      },
      
      // Search debounce
      searchTimeout: null,
    }
  },
  
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.rowsPerPage)
    },
    
    pendingCount() {
      return this.totalItems
    },
    
    hasActiveFilters() {
      return !!(
        this.filter.search_keyword ||
        this.filter.category_id
      )
    },
    
    rejectRemarkRules() {
      return [
        v => !!v || 'Rejection reason is required',
        v => (v && v.length >= 5) || 'Rejection reason must be at least 5 characters',
        v => (v && v.length <= 1000) || 'Rejection reason must be 1000 characters or less',
      ]
    },
  },
  
  watch: {
    currentPage() {
      this.fetchProducts()
    },
    rowsPerPage() {
      this.currentPage = 1
      this.fetchProducts()
    },
  },
  
  async mounted() {
    await this.loadCategories()
    await this.fetchProducts()
  },
  
  methods: {
    async fetchProducts() {
      try {
        this.loading = true
        
        const params = {
          page: this.currentPage,
          rowsPerPage: this.rowsPerPage,
          ...this.filter,
        }
        
        // Handle sort parameters
        if (this.filter.sort_by.includes('_desc')) {
          params.sort_by = this.filter.sort_by.replace('_desc', '')
          params.sort_order = 'desc'
        } else {
          params.sort_by = this.filter.sort_by
          params.sort_order = 'asc'
        }
        
        const response = await axios.get('products/pending_approvals', { params })
        
        if (response.data.success) {
          this.products = response.data.data
          this.totalItems = response.data.count
        }
      } catch (error) {
        console.error('Error fetching pending products:', error)
        this.showSnackbar('Error loading pending products', 'error')
      } finally {
        this.loading = false
      }
    },
    
    async loadCategories() {
      try {
        const response = await axios.get('products/categories')
        if (response.data.success) {
          this.categories = response.data.data
        }
      } catch (error) {
        console.error('Error loading categories:', error)
      }
    },
    
    // Filter methods
    handleSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.fetchProducts()
      }, 500)
    },
    
    applyFilters() {
      this.currentPage = 1
      this.fetchProducts()
    },
    
    clearAllFilters() {
      this.filter = {
        search_keyword: '',
        category_id: null,
        sort_by: 'created_at',
      }
      this.applyFilters()
    },
    
    getActiveFiltersCount() {
      let count = 0
      if (this.filter.search_keyword) count++
      if (this.filter.category_id) count++
      return count
    },
    
    // Pagination methods
    handlePageChange() {
      this.fetchProducts()
    },
    
    handlePageSizeChange() {
      this.currentPage = 1
      this.fetchProducts()
    },
    
    // Dialog methods
    openApproveDialog(product) {
      this.approveDialog = {
        show: true,
        product: product,
        remark: '',
        loading: false,
      }
    },
    
    closeApproveDialog() {
      this.approveDialog = {
        show: false,
        product: null,
        remark: '',
        loading: false,
      }
    },
    
    openRejectDialog(product) {
      this.rejectDialog = {
        show: true,
        product: product,
        remark: '',
        loading: false,
        errors: {},
      }
    },
    
    closeRejectDialog() {
      this.rejectDialog = {
        show: false,
        product: null,
        remark: '',
        loading: false,
        errors: {},
      }
    },
    
    // Approval methods
    async approveProduct() {
      try {
        this.approveDialog.loading = true
        this.loadingActions[this.approveDialog.product.id] = true
        
        const data = {
          admin_remark: this.approveDialog.remark || null,
        }
        
        const response = await axios.post(`products/approve/${this.approveDialog.product.id}`, data)
        
        if (response.data.success) {
          this.showSnackbar('Product approved successfully', 'success')
          this.closeApproveDialog()
          await this.fetchProducts()
        }
      } catch (error) {
        console.error('Error approving product:', error)
        this.showSnackbar('Error approving product', 'error')
      } finally {
        this.approveDialog.loading = false
        delete this.loadingActions[this.approveDialog.product?.id]
      }
    },
    
    async rejectProduct() {
      try {
        this.rejectDialog.loading = true
        this.rejectDialog.errors = {}
        this.loadingActions[this.rejectDialog.product.id] = true
        
        const data = {
          admin_remark: this.rejectDialog.remark,
        }
        
        const response = await axios.post(`products/reject/${this.rejectDialog.product.id}`, data)
        
        if (response.data.success) {
          this.showSnackbar('Product rejected successfully', 'success')
          this.closeRejectDialog()
          await this.fetchProducts()
        }
      } catch (error) {
        console.error('Error rejecting product:', error)
        if (error.response?.data?.errors) {
          this.rejectDialog.errors = error.response.data.errors
        } else {
          this.showSnackbar('Error rejecting product', 'error')
        }
      } finally {
        this.rejectDialog.loading = false
        delete this.loadingActions[this.rejectDialog.product?.id]
      }
    },
    
    // Utility methods
    getFirstProductImage(product) {
      return product && product.images && product.images.length > 0 ? product.images[0] : null
    },
    
    getImageUrl(image) {
      if (!image || !image.image_url) return null
      // Assuming your image URLs are stored as paths and need base URL
      return `${process.env.VUE_APP_STORAGE_URL}/${image.image_url}`
    },
    
    getStockColor(stock) {
      const stockNum = parseFloat(stock)
      if (stockNum === 0) return 'error'
      if (stockNum <= 10) return 'warning'
      return 'success'
    },
    
    formatDate(dateString) {
      return this.$options.filters.formatDate(dateString)
    },
    
    formatTime(dateString) {
      return this.$options.filters.formatTime(dateString)
    },
    
    showSnackbar(message, color = 'success') {
      this.successSnackbar = {
        show: true,
        message: message,
        color: color,
      }
    },
  },
}
</script>

<style scoped>
.v-data-table >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02) !important;
}
</style>